/* Core & libraries */
import React from "react";
import csvDownload from "json-to-csv-export";
import { useIntl } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch } from "../endpoints/base";
import {
  CREATE_GUEST_PROFILE_ENDPOINT,
  DELETE_GUEST_PROFILE_ENDPOINT,
  GET_GUEST_PROFILE_ENDPOINT,
  GET_GUEST_PROFILES_ENDPOINT,
  GUEST_PROFILES_COUNT_ENDPOINT,
  SEARCH_GUEST_PROFILES_ENDPOINT,
  UPDATE_GUEST_PROFILE_ENDPOINT,
  DOWNLOAD_GUEST_PROFILES_DATA_ENPOINT
} from '../endpoints/endpoints';
import { FormattedMessage } from "react-intl";
import { useNotificationContext } from "../contexts/notificationContext";
import { useUserContext } from "../contexts/userContext";
import { getHeadersForCsv } from "helpers/general";

/* Components */
/* Styles */

export const useGuestProfileService = () => {
  const { genericApiFetch } = useGenericApiFetch();
  const { setNotification } = useNotificationContext();
  const userContext = useUserContext();
  const companyEntityId = userContext?.userInfo?.companyInfo?.companyEntityId;
  const providerCode = userContext?.userInfo?.providerInfo?.providerCode;
  const intl = useIntl();
  
  const getGuestProfiles = async (options) => {
    if (options) {
      let filters = "";
      if (options.filters) {
        for (const [key, value] of Object.entries(options.filters)) {
          value.map(item => {
            if (item) {
              filters += `${filters ? "," : ""}${key}-${item}`
            }
          })
        }
      }
      options = {
        ...options,
        filters
      }
      
      const response = await genericApiFetch({
        url: GET_GUEST_PROFILES_ENDPOINT.replace('{companyEntityId}', companyEntityId),
        method: "GET",
        queryParams: options,
        spinner: "guestProfilesGridSpinner",
      });

      if (response.success) {
        return response.data;
      } else {
        return { results: null };
      }
    };
  }

  const searchProfile = async (searchTerm) => {
      const response = await genericApiFetch({
        url: SEARCH_GUEST_PROFILES_ENDPOINT.replace('{companyEntityId}', companyEntityId),
        method: "GET",
        queryParams: {searchTerm},
        spinner: "searchGridFilters"
      });

      if (response.success) {
        return response.data?.users
      }
      return false
  }
  
  const getGuestProfile = async (guestProfileId) => {
    const response = await genericApiFetch({
      url: GET_GUEST_PROFILE_ENDPOINT.replace('{companyEntityId}', companyEntityId).replace('{userEntityId}', guestProfileId),
      method: "GET",
    });
    
    if (response.success) {
      return response.data
    }
    return false
  };
  
  const createGuestProfile = async (payload) => {
    const response = await genericApiFetch({
      url: CREATE_GUEST_PROFILE_ENDPOINT.replace('{companyEntityId}', companyEntityId),
      method: "POST",
      data: payload,
      spinner: "guestProfilePage",
    });
    
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="guestProfileService.successTitle"/>,
        <FormattedMessage id="guestProfileService.successText"/>
      );
      return response.data
    } else if (response?.errorMessages[0]?.code === "13001") {
      setNotification(
        "error",
        <FormattedMessage id="guestProfileService.alreadyExistTitle"/>,
        <FormattedMessage id="guestProfileService.alreadyExistText"/>
      );
      return false
    }
    setNotification(
      "error",
      <FormattedMessage id="guestProfileService.errorTitle"/>,
      <FormattedMessage id="guestProfileService.errorText"/>
    );
    return false
  };
  
  const updateGuestProfile = async (payload) => {
    const response = await genericApiFetch({
      url: UPDATE_GUEST_PROFILE_ENDPOINT.replace('{companyEntityId}', companyEntityId).replace('{userEntityId}', payload?.userEntityId),
      method: "PATCH",
      data: payload,
      spinner: "guestProfilePage",
    });

    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="guestProfileService.updateGuestProfileSuccessTitle" />,
        <FormattedMessage id="guestProfileService.updateGuestProfileSuccessText" />
      )
      return true;
    } else if (response?.errorMessages[0]?.code === "13001") {
      setNotification(
        "error",
        <FormattedMessage id="guestProfileService.alreadyExistTitle"/>,
        <FormattedMessage id="guestProfileService.alreadyExistText"/>
      );
      return false
    }
    setNotification(
      "error",
      <FormattedMessage id="guestProfileService.errorTitle"/>,
      <FormattedMessage id="guestProfileService.errorText"/>
    );
    return false
  };
  
  const deleteGuestProfile = async (guestProfileId) => {
    const response = await genericApiFetch({
      url: DELETE_GUEST_PROFILE_ENDPOINT.replace('{companyEntityId}', companyEntityId).replace('{userEntityId}', guestProfileId),
      method: "DELETE",
    });
    
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notification.deleteGuestProfileSuccessTitle" />,
        <FormattedMessage id="notification.deleteGuestProfileSuccessText" />
      )
      return true;
    }
    setNotification(
      "error",
      <FormattedMessage id="guestProfileService.errorTitle"/>,
      <FormattedMessage id="guestProfileService.errorText"/>
    );
    return false
  };

  const downloadGuestUsersGridData =  async () => {
    const response = await  genericApiFetch({
      url: DOWNLOAD_GUEST_PROFILES_DATA_ENPOINT.replace("{providerCode}", providerCode.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "GET",
      spinner: "downloadGuestProfilesGridData",
    })
    if (response.success) {
      csvDownload({
        data: response.data.items,
        filename: response.data.fileName,
        delimiter: ",",
        fileType: "text/csv",
        headers: getHeadersForCsv(response.data?.items, "guestGrid", intl)
      });
      return true
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.reportErrorTitle" />,
        <FormattedMessage id="notifications.reportErrorText" />
      )
      return false
    }
  };
  
  const getGuestProfilesCount = async (guestProfileId) => {
    const response = await genericApiFetch({
      url: GUEST_PROFILES_COUNT_ENDPOINT.replace('{companyEntityId}', companyEntityId),
      method: "GET",
    });

    if (response.success) {
      return response?.data?.guestProfileCount
    }
    return false;
  };

  return {
    getGuestProfiles,
    getGuestProfilesCount,
    searchProfile,
    getGuestProfile,
    createGuestProfile,
    updateGuestProfile,
    deleteGuestProfile,
    downloadGuestUsersGridData
  };
}

